import CheckoutForm from '@/components/v1/CheckoutForm'
import FunnelLogo from '@/components/v1/FunnelLogo'
import { type GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import { type Plan } from '@app-studio-web/ui/types'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, type StripeElementLocale } from '@stripe/stripe-js'
import { type User } from '@supabase/supabase-js'
import { useTranslation } from 'react-i18next'
import { Navigate, useLoaderData, useSearchParams } from 'react-router-dom'

// Locales Stripe Elements can localize placeholders and validation messages in.
// Kept in sync with the StripeElementLocale union from @stripe/stripe-js.
const stripeSupportedLocales = new Set<StripeElementLocale>([
  'ar',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'et',
  'fi',
  'fil',
  'fr',
  'he',
  'hr',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'lt',
  'lv',
  'ms',
  'mt',
  'nb',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'th',
  'tr',
  'vi',
  'zh',
])

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
)

/**
 * li-funnel-redesign v1 — the editorial payment step. The shared subscription
 * action always redirects to `/checkout/payment`, so this page takes no props
 * and reads the intent off the URL exactly like the shared one does.
 */
const PLANS_PATH = '/checkout/plans'

export default function PaymentPage() {
  const [searchParams] = useSearchParams()
  const [pricesData, user] = useLoaderData() as [GetPricesData, User]

  const clientSecret = searchParams.get('client_secret')!
  const currency = searchParams.get('currency')!
  const intentMode =
    searchParams.get('intent_mode') === 'setup' ? 'setup' : 'payment'
  const plan = searchParams.get('plan')! as Plan
  const subscriptionId = searchParams.get('subscription_id') ?? ''
  const trialPeriodDaysParam = searchParams.get('trial_period_days')
  const trialPeriodDays = trialPeriodDaysParam
    ? Number.parseInt(trialPeriodDaysParam, 10)
    : undefined

  const { i18n, t } = useTranslation()

  const stripeLocale: StripeElementLocale = stripeSupportedLocales.has(
    i18n.language as StripeElementLocale,
  )
    ? (i18n.language as StripeElementLocale)
    : 'auto'

  if (!clientSecret) return <Navigate replace to={PLANS_PATH} />

  return (
    <div className="bg-hw-cream text-hw-ink min-h-dvh">
      {/*
        No back affordance here, deliberately. Every other payment page in the
        monorepo omits one, and sending the buyer back to the sales page
        abandons the Stripe intent in the URL: re-picking a plan runs
        `createStripeSubscriptionAction` again and orphans this one.
      */}
      <header className="hw-container flex h-16 items-center justify-center">
        <FunnelLogo />
      </header>

      <div className="hw-container space-y-5 pt-2 pb-12">
        <h1 className="hw-heading animate-fade-up text-left!">
          {t('pages.checkout.v1.paymentHeading')}
        </h1>

        <Elements
          options={{
            appearance: {
              labels: 'floating',
              theme: 'stripe',
              variables: {
                borderRadius: '12px',
                colorBackground: '#FFFFFF',
                colorDanger: '#E5484D',
                colorPrimary: '#006BFF',
                colorText: '#16171C',
                colorTextSecondary: '#6E7180',
              },
            },
            clientSecret,
            locale: stripeLocale,
          }}
          stripe={stripePromise}
        >
          <CheckoutForm
            clientSecret={clientSecret}
            currency={currency}
            intentMode={intentMode}
            plan={plan}
            pricesData={pricesData}
            subscriptionId={subscriptionId}
            trialPeriodDays={trialPeriodDays}
            userEmail={user?.user_metadata?.original_email ?? ''}
          />
        </Elements>
      </div>
    </div>
  )
}
